import { FormGroup, NumericInput, InputGroup, Dialog } from '@blueprintjs/core'
import { useState, useEffect, useCallback } from 'react'
import { Button } from 'components/Button'
import { runParameterDetection } from 'connections/controller'
import { Colors } from 'design/colors'
import { useDevices } from 'hooks/useDevices'
import { Strings, versionLessThan } from 'utils/common'
import {
  IStartParamDetectResponse,
  IStartParamDetectRequest,
  displayUnits,
  PacketId
} from 'utils/common-types'
import { MIN_FW_FOR_PARAM_DETECT_V2 } from 'utils/firmware-versions'
import {
  INavigationProps,
  Edit,
  paramIsNonNegativeNumber,
  FORM_WIDTH,
  labelStyle,
  valueStyle,
  inputStyle,
  configContainerStyle,
  configModalStyle,
  screenTitleStyle,
  inputContStyle
} from './CStoreEditor'

export const ParamDetectScreen = (
  props: INavigationProps & {
    paramDetect: IStartParamDetectResponse | undefined
    setParamDetect: (value: IStartParamDetectResponse) => void
    manualParamInput: boolean
    setManualParamInput: (value: boolean) => void
    manualParamDetect: Strings<IStartParamDetectResponse>
    editManualParamConfig: Edit<IStartParamDetectResponse>
    userSpecifiedParams: Strings<IStartParamDetectRequest>
    editUserSpecifiedParams: Edit<IStartParamDetectRequest>
    polePairCount: number
  }
) => {
  const { connectedDevice } = useDevices()
  const [isRunning, setIsRunning] = useState<boolean>(false)
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false)
  const [paramDetectErrorMessage, setParamDetectErrorMessage] = useState<string | undefined>()
  const {
    next,
    prev,
    setEnableNavigation,
    attached,
    paramDetect,
    setParamDetect,
    manualParamInput,
    setManualParamInput,
    manualParamDetect,
    editManualParamConfig,
    userSpecifiedParams,
    editUserSpecifiedParams,
    polePairCount
  } = props

  useEffect(() => {
    setEnableNavigation(!isRunning)
  }, [setEnableNavigation, isRunning])

  // Clear error message upon exiting this screen
  useEffect(() => {
    return () => {
      setParamDetectErrorMessage(undefined)
    }
  }, [])

  const buttonText = paramDetect === undefined ? 'Start' : 'Re-run'
  const buttonBackgroundColor = paramDetect === undefined ? Colors.BUTTON_GREEN : Colors.WHITE
  const buttonTextColor = paramDetect === undefined ? Colors.WHITE : Colors.BLACK

  const productName = connectedDevice?.handle.productName.toLowerCase()
  const minFwForParamDetectV2 = Object.entries(MIN_FW_FOR_PARAM_DETECT_V2).find((entry) =>
    productName?.includes(entry[0].toLowerCase())
  )?.[1]

  const paramDetectV2Available =
    connectedDevice?.initialized && minFwForParamDetectV2
      ? !versionLessThan(connectedDevice.fwVersion, minFwForParamDetectV2)
      : false

  const areUserSpecifiedParamsValid =
    paramDetectV2Available &&
    paramIsNonNegativeNumber(userSpecifiedParams.paramDetectMaxRpm) &&
    paramIsNonNegativeNumber(userSpecifiedParams.paramDetectMaxCurrent)

  const run = useCallback(async () => {
    if (connectedDevice === undefined) {
      return
    }

    setParamDetectErrorMessage(undefined) // Clear error message, just in case
    setIsRunning(true)
    try {
      const userSpecifiedParamsBody: IStartParamDetectRequest = {
        paramDetectMaxRpm: parseFloat(userSpecifiedParams.paramDetectMaxRpm),
        paramDetectMaxCurrent: parseFloat(userSpecifiedParams.paramDetectMaxCurrent),
        motorIndex: 0
      }

      const polePairCountValue = paramDetectV2Available ? polePairCount : undefined

      const response = await runParameterDetection(
        connectedDevice.handle,
        userSpecifiedParamsBody,
        0, // TODO: implement motorindex for multi-motor parameter detection
        polePairCountValue
      )
      const paramDetectResults = {
        ...response,
        ...userSpecifiedParamsBody
      }

      setParamDetect(paramDetectResults)
    } catch (e) {
      // ToDo: consider showing this to user
      console.warn(e)
      setParamDetectErrorMessage(`${e}`)
    } finally {
      setIsRunning(false)
    }
  }, [
    connectedDevice,
    paramDetectV2Available,
    polePairCount,
    setParamDetect,
    userSpecifiedParams.paramDetectMaxCurrent,
    userSpecifiedParams.paramDetectMaxRpm
  ])

  const getValue = useCallback(
    (key: keyof NonNullable<typeof paramDetect>) => {
      /* commenting this out so that the values update as they are generated
        if ( isRunning ) {
            return <LoadingDots />;
        }
        */
      return paramDetect?.[key].toExponential(6) || null
    },
    [paramDetect]
  )

  const handleManualInputToggle = () => {
    if (manualParamInput) {
      setManualParamInput(false)
    } else {
      setShowWarningModal(true)
    }
  }

  return (
    <>
      <div style={configContainerStyle}>
        <div style={screenTitleStyle}>
          {manualParamInput
            ? 'Parameter Detection - (Manual Input)'
            : 'Automated Parameter Detection'}
        </div>
        {/* Manual entry */}
        {paramDetectV2Available ? (
          <>
            {/* Note label */}
            <div
              style={{
                color: Colors.WHITE,
                marginBottom: 16,
                paddingLeft: 8,
                paddingRight: 8,
                display: 'flex'
              }}
            >
              <div style={{ fontWeight: 'bold' }}>Note:&nbsp;</div> Please provide the following
              characteristics for the connected motor.
            </div>
            {/* Form area */}
            <div
              style={{
                width: FORM_WIDTH,
                maxWidth: '100%',
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'start',
                flexDirection: 'column'
              }}
            >
              <FormGroup
                label="Max Rated Motor Speed (RPM)"
                labelInfo={<span style={{ color: Colors.RED }}>*</span>}
              >
                <NumericInput
                  fill={true}
                  value={userSpecifiedParams.paramDetectMaxRpm}
                  onValueChange={(_, valStr) =>
                    editUserSpecifiedParams('paramDetectMaxRpm', valStr)
                  }
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder="3000 RPM"
                  style={inputStyle}
                />
              </FormGroup>
              <FormGroup
                label="Max Rated Motor Current (A)"
                labelInfo={<span style={{ color: Colors.RED }}>*</span>}
              >
                <NumericInput
                  fill={true}
                  value={userSpecifiedParams.paramDetectMaxCurrent}
                  onValueChange={(_, valStr) =>
                    editUserSpecifiedParams('paramDetectMaxCurrent', valStr)
                  }
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder="5 A"
                  style={inputStyle}
                />
              </FormGroup>
            </div>
          </>
        ) : null}
        {manualParamInput ? (
          <div style={inputContStyle}>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>
                Resistance ({displayUnits[PacketId.START_PARAM_DETECT].resistance}):
              </div>
              <InputGroup
                style={valueStyle}
                value={manualParamDetect.resistance}
                onChange={(e) => editManualParamConfig('resistance', e.target.value)}
              />
            </div>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>Ld ({displayUnits[PacketId.START_PARAM_DETECT].ld}):</div>
              <InputGroup
                style={valueStyle}
                value={manualParamDetect.ld}
                onChange={(e) => editManualParamConfig('ld', e.target.value)}
              />
            </div>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>Lq ({displayUnits[PacketId.START_PARAM_DETECT].lq}):</div>
              <InputGroup
                style={valueStyle}
                value={manualParamDetect.lq}
                onChange={(e) => editManualParamConfig('lq', e.target.value)}
              />
            </div>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>
                Flux Linkage ({displayUnits[PacketId.START_PARAM_DETECT].fluxLinkage}):
              </div>
              <InputGroup
                style={valueStyle}
                value={manualParamDetect.fluxLinkage}
                onChange={(e) => editManualParamConfig('fluxLinkage', e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div style={inputContStyle}>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>
                Resistance ({displayUnits[PacketId.START_PARAM_DETECT].resistance}):
              </div>
              <div style={valueStyle}>{getValue('resistance')}</div>
            </div>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>Ld ({displayUnits[PacketId.START_PARAM_DETECT].ld}):</div>
              <div style={valueStyle}>{getValue('ld')}</div>
            </div>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>Lq ({displayUnits[PacketId.START_PARAM_DETECT].lq}):</div>
              <div style={valueStyle}>{getValue('lq')}</div>
            </div>
            <div style={{ flex: 1, display: 'flex' }}>
              <div style={labelStyle}>
                Flux Linkage ({displayUnits[PacketId.START_PARAM_DETECT].fluxLinkage}):
              </div>
              <div style={valueStyle}>{getValue('fluxLinkage')}</div>
            </div>
          </div>
        )}
        {/* Warning message about running unloaded and emitting a loud noise */}
        <div
          style={{
            color: Colors.YELLOW,
            paddingLeft: 8,
            paddingRight: 8,
            display: 'flex'
          }}
        >
          <div style={{ fontWeight: 'bold' }}>Warning:&nbsp;</div> After clicking "{buttonText}"
          below, the connected motor will emit noise and spin.
        </div>
        <div
          style={{
            color: Colors.YELLOW,
            marginBottom: 16,
            paddingLeft: 8,
            paddingRight: 8,
            display: 'flex'
          }}
        >
          Please ensure the connected motor is <b>&nbsp;unloaded&nbsp;</b>
          before proceeding.
        </div>
        {/* Back and next buttons */}
        <div style={{ display: 'flex', width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.WHITE}
              textColor={Colors.BLACK}
              onClick={prev}
              disabled={prev === undefined}
            >
              Back
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={buttonBackgroundColor}
              textColor={buttonTextColor}
              disabled={
                !attached ||
                connectedDevice === undefined ||
                isRunning ||
                (paramDetectV2Available && !areUserSpecifiedParamsValid) // disabled if invalid user-specified params
              }
              onClick={run}
            >
              {buttonText}
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.BUTTON_GREEN}
              textColor={Colors.WHITE}
              onClick={next}
              disabled={next === undefined}
            >
              Next
            </Button>
          </div>
        </div>
        {/* Manual input buttons */}
        <div style={{ marginTop: 16, display: 'flex', width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.YELLOW}
              textColor={Colors.BLACK}
              onClick={handleManualInputToggle}
            >
              {manualParamInput ? 'Disable Manual Input' : 'Enable Manual Input'}
            </Button>
          </div>
        </div>
      </div>
      <Dialog
        title={'Parameter Detection Failure'}
        isOpen={Boolean(paramDetectErrorMessage)}
        onClose={() => setParamDetectErrorMessage(undefined)}
        style={{ backgroundColor: Colors.LIGHT_GREEN }}
      >
        <div style={configModalStyle}>
          <p>
            A problem was encountered during parameter detection:
            <br />
            {paramDetectErrorMessage}
          </p>
        </div>
      </Dialog>
      <Dialog
        title="Warning"
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        style={{ backgroundColor: Colors.LIGHT_GREEN }}
      >
        <div style={configModalStyle}>
          <p>
            {' '}
            This is not recommended, and incorrect values may result in damage to the controller.
            Please contact Salient Motion for support. Do you wish to continue?
          </p>
          <div style={{ alignSelf: 'flex-end', display: 'flex' }}>
            <Button
              backgroundColor={Colors.RED}
              textColor={Colors.BLACK}
              style={{ marginRight: 10 }}
              onClick={() => {
                setShowWarningModal(false)
                setManualParamInput(false)
              }}
            >
              Cancel
            </Button>
            <Button
              backgroundColor={Colors.WHITE}
              textColor={Colors.BLACK}
              onClick={() => {
                setShowWarningModal(false)
                setManualParamInput(true)
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
